/**
 * Web Push, browser side.
 *
 * The split of duties: the Gateway owns the VAPID key pair and does the
 * sending, the browser's push service owns the endpoint, and this app only
 * carries values between them. Nothing secret passes through here, which is why
 * a client with no backend can do this at all.
 *
 * Everything is feature-detected. Push needs a service worker, the Push API and
 * a granted permission — on iOS all three only exist once the app has been
 * added to the home screen, so "not available" is a normal state to report
 * rather than an error to throw.
 */

export type PushSupport =
  | { supported: true }
  | { supported: false; reason: string };

export function detectPushSupport(): PushSupport {
  if (!("serviceWorker" in navigator)) {
    return { supported: false, reason: "Dieser Browser hat keinen Service Worker." };
  }
  if (!("PushManager" in window)) {
    // Safari on iOS only exposes PushManager to an installed web app.
    return {
      supported: false,
      reason:
        "This browser cannot do web push. On an iPhone it only works once cllw has been installed through “Add to Home Screen”.",
    };
  }
  if (!("Notification" in window)) {
    return { supported: false, reason: "Dieser Browser kennt keine Benachrichtigungen." };
  }
  return { supported: true };
}

/** VAPID keys travel as base64url; `applicationServerKey` wants raw bytes. */
export function decodeVapidKey(base64Url: string): Uint8Array {
  const padded = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), "=");
  const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/** The two public values the Gateway needs to encrypt for this endpoint. */
export function readSubscriptionKeys(subscription: PushSubscription): {
  endpoint: string;
  keys: { p256dh: string; auth: string };
} | null {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) {
    return null;
  }
  return { endpoint: json.endpoint, keys: { p256dh, auth } };
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

/**
 * Subscribes this browser to the Gateway's push service.
 *
 * `userVisibleOnly` is not optional in any current browser: a subscription that
 * does not promise a visible notification is refused outright.
 */
export async function createSubscription(vapidKey: string): Promise<PushSubscription> {
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    return existing;
  }
  return await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeVapidKey(vapidKey) as BufferSource,
  });
}
